import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, onIdTokenChanged, User } from 'firebase/auth';
import { FirmDatabase } from '../database/firmDatabase';
import { auth, db } from '../firebaseConfig';
import { telemetry } from '@/lib/telemetry';
import { v4 as uuidv4 } from 'uuid';
import { AuthenticatedSession } from '../authenticatedSession/authenticatedSession';
import { AuthenticatedSessionContext } from '../authenticatedSession/AuthenticatedSessionContext';
import { UserDocument } from '@/database/baseTypes';
import {
  setCrossDomainAuthCookie,
  clearCrossDomainAuthCookie,
} from '../utils/crossDomainAuth';

export type AuthSessionState =
  | {
      // When the browser is refreshed, the auth state resets and is
      // unknown for a moment. Without the 'unknown' status, we'll enter
      // the "unauthenticated" path temporarily and redirect the user to the
      // login page, losing the current path.
      status: 'unknown';
    }
  | {
      status: 'authenticated';
      authSession: AuthenticatedSession;
    }
  | {
      status: 'unauthenticated';
    };

const AuthSessionStateContext = createContext<AuthSessionState | undefined>(
  undefined,
);

// Owns the Firebase auth subscriptions (onAuthStateChanged, onIdTokenChanged,
// visibilitychange) and the Firestore user-doc subscription. Mount once at
// the root so the subscriptions are not duplicated across consumers.
export const AuthSessionStateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const authUserState = useAuthUserState();
  const authUser =
    authUserState.status === 'authenticated' ? authUserState.user : undefined;

  // The primary reason for observing the firestore user here is to know when
  // the user's authId has been set after registering. Firebase's `onAuthStateChanged`
  // function is called as soon as we create the user, before the authId is set,
  // so we fail to find the user the first time `onAuthStateChanged` is called.
  //
  // It's also just nice to know when this info changes, so that we can include
  // the accurate info in the AuthenticatedSession object.
  const firestoreUser = useFirestoreUser({
    tenantId: authUser?.tenantId ?? undefined,
    authUserId: authUser?.uid,
  });

  const [authState, setAuthState] = useState<AuthSessionState>({
    status: 'unknown',
  });

  useEffect(() => {
    if (authUserState.status === 'unauthenticated') {
      setAuthState({ status: 'unauthenticated' });
      setUnauthenticatedTelemetryData();
      clearCrossDomainAuthCookie();
      return;
    }

    if (
      authUserState.status === 'unknown' ||
      !firestoreUser ||
      !authUser?.email ||
      !authUser?.tenantId
    ) {
      setAuthState({ status: 'unknown' });
      return;
    }

    const authSession = {
      sessionId: uuidv4(),
      tenantId: authUser.tenantId,
      userIds: {
        auth: authUser.uid,
        firestore: firestoreUser.user.id,
      },
      userEmail: authUser.email,
      db: new FirmDatabase(authUser.tenantId, db),
    };
    setAuthState({
      status: 'authenticated',
      authSession,
    });
    setAuthenticatedTelemetryData(authSession);
    // Set cookie for cross-domain auth detection (accessible on practist.io)
    setCrossDomainAuthCookie(authUser.email);
  }, [
    authUserState.status,
    authUser?.uid,
    authUser?.email,
    authUser?.tenantId,
    firestoreUser?.user.id,
  ]);

  return (
    <AuthSessionStateContext.Provider value={authState}>
      <AuthenticatedSessionContext.Provider
        value={
          authState.status === 'authenticated'
            ? authState.authSession
            : undefined
        }
      >
        {children}
      </AuthenticatedSessionContext.Provider>
    </AuthSessionStateContext.Provider>
  );
};

export const useAuthSessionState = (): AuthSessionState => {
  const value = useContext(AuthSessionStateContext);
  if (!value) {
    throw new Error(
      'useAuthSessionState must be used within an AuthSessionStateProvider',
    );
  }
  return value;
};

type AuthUserState =
  | { status: 'unknown' }
  | { status: 'authenticated'; user: User }
  | { status: 'unauthenticated' };

const useAuthUserState = () => {
  const [state, setState] = useState<AuthUserState>({ status: 'unknown' });
  useEffect(() => {
    // Check if user is already authenticated on mount
    const currentUser = auth.currentUser;
    if (currentUser) {
      setState({ status: 'authenticated', user: currentUser });
    }

    // Listen to auth state changes (login/logout)
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setState({ status: 'authenticated', user });
        telemetry.info({
          message: 'User authenticated',
          extra: {
            userId: user.uid,
            email: user.email,
            tenantId: user.tenantId,
          },
        });
      } else {
        console.log('[useAuthUserState] User logged out');
        telemetry.warn({
          message: 'User logged out (auth state changed to null)',
        });
        setState({ status: 'unauthenticated' });
      }
    });

    // Listen to token changes (including refresh events)
    // This is critical for handling token expiration and refresh
    // Firebase automatically refreshes tokens, but we listen here to:
    // 1. Ensure tokens stay fresh across multiple tabs
    // 2. Log token refresh events for debugging
    // 3. Update the user object when tokens refresh (user object may have updated claims)
    const unsubscribeToken = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          // Get the latest token (this will refresh if needed)
          // We don't force refresh here - Firebase handles that automatically
          // This just ensures we have the latest token and user object
          await user.getIdToken(false);
          console.log('[useAuthUserState] Token updated/refreshed');

          // Update state with the latest user object
          // The user object may have updated claims after token refresh
          setState((prevState) => {
            // Only update if we're already authenticated for this user
            // This prevents race conditions with onAuthStateChanged
            if (
              prevState.status === 'authenticated' &&
              prevState.user?.uid === user.uid
            ) {
              return { status: 'authenticated', user };
            }
            // If not authenticated yet, onAuthStateChanged will handle it
            return prevState;
          });
        } catch (error) {
          console.error('[useAuthUserState] Token refresh failed:', error);
          telemetry.error({
            error: error as Error,
            message: 'Firebase token refresh failed',
            extra: {
              userId: user.uid,
              email: user.email,
              tenantId: user.tenantId,
            },
          });
          // Don't log out on token refresh failure - let onAuthStateChanged handle it
          // If the token is truly invalid, Firebase will call onAuthStateChanged with null
        }
      } else {
        console.log(
          '[useAuthUserState] Token changed - user is null (logged out)',
        );
      }
    });

    // Refresh token when tab becomes visible again
    // This helps prevent logouts when users return to backgrounded tabs
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        console.log('[useAuthUserState] Tab became visible, refreshing token');
        auth.currentUser
          .getIdToken(true)
          .then(() => {
            console.log(
              '[useAuthUserState] Token refreshed after visibility change',
            );
            telemetry.info({
              message: 'Token refreshed after tab visibility change',
              extra: {
                userId: auth.currentUser?.uid,
                email: auth.currentUser?.email,
              },
            });
          })
          .catch((error) => {
            console.error(
              '[useAuthUserState] Token refresh failed after visibility change:',
              error,
            );
            telemetry.error({
              error,
              message: 'Firebase token refresh failed after visibility change',
              extra: {
                userId: auth.currentUser?.uid,
                email: auth.currentUser?.email,
              },
            });
          });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeAuth();
      unsubscribeToken();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return state;
};

const useFirestoreUser = (props: {
  tenantId: string | undefined;
  authUserId: string | undefined;
}) => {
  const [firestoreUser, setFirestoreUser] = useState<{
    user: UserDocument;
    updatedAt: number;
  }>();

  useEffect(() => {
    if (!props.tenantId || !props.authUserId) {
      setFirestoreUser(undefined);
      return;
    }
    const firmDb = new FirmDatabase(props.tenantId, db);
    return firmDb.subscribeToUsersChanges(
      (snapshot) => {
        if (snapshot.docs.length !== 1) {
          setFirestoreUser(undefined);
          return;
        }
        const userData = snapshot.docs[0].data();
        setFirestoreUser({
          user: { ...userData, id: snapshot.docs[0].id },
          updatedAt: Date.now(),
        });
      },
      [['authId', '==', props.authUserId]],
    );
  }, [props.tenantId, props.authUserId]);

  return firestoreUser;
};

const setAuthenticatedTelemetryData = (authSession: AuthenticatedSession) => {
  telemetry.setUserProperties({
    userIds: authSession.userIds,
    email: authSession.userEmail,
    tenantId: authSession.tenantId,
  });
  telemetry.analyticsEvent({
    name: 'auth_session_created',
  });
};

const setUnauthenticatedTelemetryData = () => {
  telemetry.analyticsEvent({
    name: 'auth_session_missing',
  });
  telemetry.clearUserProperties();
};
